import { For, Show } from 'solid-js'
import type { HubCheckIn } from '../../lib/hub'

/**
 * Recent activity — the guest's last few visits at a glance.
 *
 * This was inert: a venue, a date, an item count and a price, where the count
 * ignored returns. A visit where the guest took one coat and got it back looked
 * identical to one still in the cloakroom, and "See all" opened a page with the
 * same thin rows.
 *
 * Each row now answers the four questions a guest actually has — where, when,
 * what it cost, and what came back — and the whole row opens the detailed
 * activity entry where every item is listed with its tag and return time.
 */
export default function RecentHistoryCard(props: {
  recentHistory: HubCheckIn[]
  onSeeAll: () => void
  onOpen?: (checkInId: string) => void
}) {
  const euros = (cents: number | null | undefined) => `€${((cents ?? 0) / 100).toFixed(2)}`

  const when = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  }

  const clock = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }

  /** Items the venue handed back. */
  const returned = (c: HubCheckIn) =>
    (c.items ?? []).filter((i) => i.status === 'checked_out' || i.status === 'returned').length

  return (
    <section class="rounded-2xl bg-white/5 border border-white/10 p-5">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-sm font-semibold text-white/80">Recent activity</h2>
        <button
          type="button"
          onClick={() => props.onSeeAll()}
          class="text-xs text-emerald-300 hover:text-emerald-200 transition-colors"
        >
          See all
        </button>
      </div>

      <Show
        when={props.recentHistory.length > 0}
        fallback={<p class="text-sm text-white/50">Nothing here yet — your check-ins will show up.</p>}
      >
        <ul class="space-y-2">
          <For each={props.recentHistory}>
            {(checkIn) => {
              const total = (checkIn.items ?? []).length
              const back = returned(checkIn)
              return (
                <li>
                  <button
                    type="button"
                    onClick={() => props.onOpen?.(checkIn.id)}
                    class="w-full text-left rounded-lg bg-black/20 px-3 py-2.5 hover:bg-black/30 transition-colors"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <div class="min-w-0">
                        <p class="text-sm text-white truncate">{checkIn.venueName}</p>
                        <p class="text-[11px] text-white/40">
                          {when(checkIn.createdAt)} · {clock(checkIn.createdAt)}
                        </p>
                      </div>
                      <span class="text-sm font-medium text-white/70 tabular-nums shrink-0">
                        {euros(checkIn.totalAmount)}
                      </span>
                    </div>

                    {/* Returns are the part that was invisible: a visit is not
                        "2 items", it is "2 items, both returned". */}
                    <p class="mt-1 text-[11px]">
                      <Show
                        when={back > 0}
                        fallback={
                          <span class="text-emerald-300/80">
                            {total} item{total === 1 ? '' : 's'} · still at the cloakroom
                          </span>
                        }
                      >
                        <span class="text-white/40">
                          {total} item{total === 1 ? '' : 's'} · {back} returned
                        </span>
                      </Show>
                    </p>
                  </button>
                </li>
              )
            }}
          </For>
        </ul>
      </Show>
    </section>
  )
}
